"use client"

/**
 * FormDialog · 全系统统一表单弹窗壳(2026-06-15)
 *
 * 收口「每个模块各造一遍弹窗」的痛点。壳只管所有弹窗一致的部分,body 留 slot 给各模块。
 *   - 右上角关闭 X(标准关闭方式;不放左下「取消」)
 *   - 焦点陷阱已关(disableEnforceFocus/Restore)—— 内部复制按钮 textarea 焦点不被夺(docs/05 §2)
 *   - 标题:15/700 主标题 +(可选)12/secondary 副说明(docs/02 §10 句式)
 *   - 校验集中区:错误用一颗 Chip 浮在 footer 左侧,opacity 切换「不占布局流 → 位置固定不跳页」
 *     (沉淀自询单录信息弹窗);字段级红框呼吸用 errFlashSx 配合
 *   - footer 右侧只有提交按钮
 *
 * 「不太固定」:title / subtitle / maxWidth / errors / submit 系列 / contentSx / children / footerLeft 都可配,
 * 特殊弹窗(如订单号 stepper)只用 标题+关闭+body,不传 errors 即不显示校验区。
 *
 * 见 docs/08-shared-components.md。
 */

import type { ReactNode } from "react"
import { useCallback, useState } from "react"
import type { Breakpoint, SxProps, Theme } from "@mui/material"
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import CloseRoundedIcon from "@mui/icons-material/CloseRounded"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"

// 字段级红框呼吸(配合校验集中 Chip 给字段定位)。用法:sx={{ ...errFlashSx(invalid) }}
export function errFlashSx(active: boolean): SxProps<Theme> {
  if (!active) return {}
  return {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(211,47,47,0.7) !important" },
    "& .MuiOutlinedInput-root": {
      animation: "errFlash 1.5s ease-in-out infinite",
      "@keyframes errFlash": {
        "0%, 100%": { boxShadow: "0 0 0 0 rgba(211,47,47,0)" },
        "50%": { boxShadow: "0 0 0 3px rgba(211,47,47,0.22)" },
      },
    },
  }
}

// 校验集中「胶囊」(沉淀自询单录信息):错误用一颗 Chip 显示,opacity 切换不占流 → 位置固定不跳。
// FormDialog 内置用它;不便套 FormDialog 的弹窗(如 stepper)也可直接 import,保持全系统一致。
export function FormErrorChip({
  errors,
  maxWidth,
  tooltip,
  color = "error",
}: {
  errors?: (string | false | null | undefined)[]
  maxWidth?: number
  /** hover 详情(如「谁少录」),传了才包 Tooltip */
  tooltip?: ReactNode
  /** error(红,拦截类)/ warning(黄,提醒类) */
  color?: "error" | "warning"
}) {
  const text = (errors ?? []).filter(Boolean).join(" · ")
  const has = text.length > 0
  const chip = (
    <Chip
      size="small"
      color={color}
      variant="outlined"
      icon={<ErrorOutlineIcon sx={{ fontSize: 15 }} />}
      label={text}
      sx={{
        opacity: has ? 1 : 0,
        pointerEvents: has ? "auto" : "none",
        transition: "opacity 0.25s",
        ...(maxWidth ? { maxWidth } : {}),
        "& .MuiChip-label": {
          fontSize: 11.5,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
      }}
    />
  )
  return tooltip && has ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip
}

/**
 * 校验「失焦才定格」hook(沉淀自询单录信息,2026-06-15)。
 * 传入实时算出的 errors,返回定格后的 shownErrors:输入中 shownErrors 不变(不闪),
 * 在输入框 onBlur 调 commit() 才把当前判断定格;弹窗 open / 重置时调 reset()。
 *
 *   const errs = [a > b && "...", ...]
 *   const { shownErrors, commit, reset } = useDeferredErrors(errs)
 *   <TextField onBlur={commit} ... />            // 失焦才判
 *   useEffect(() => { if (open) reset() }, [open, reset])
 *   <FormErrorChip errors={shownErrors} />
 */
export function useDeferredErrors(errors: (string | false | null | undefined)[]) {
  const [shownErrors, setShownErrors] = useState<string[]>([])
  // commit 每次渲染新建(捕获最新 errors),onBlur 用没问题;reset 稳定供 effect 依赖
  const commit = () => setShownErrors(errors.filter(Boolean) as string[])
  const reset = useCallback(() => setShownErrors([]), [])
  return { shownErrors, commit, reset }
}

export function FormDialog({
  open,
  onClose,
  title,
  subtitle,
  maxWidth = "sm",
  errors = [],
  footerLeft,
  submitLabel = "确认",
  onSubmit,
  submitDisabled = false,
  submitColor = "primary",
  hideSubmit = false,
  contentSx,
  children,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  maxWidth?: Breakpoint
  /** 校验错误文案(过滤空值后用「 · 」拼成一颗集中 Chip;无错时透明占位、不跳页) */
  errors?: (string | false | null | undefined)[]
  /** 自定义 footer 左侧(传了则覆盖默认校验 Chip;给特殊弹窗用) */
  footerLeft?: ReactNode
  submitLabel?: string
  onSubmit?: () => void
  submitDisabled?: boolean
  submitColor?: "primary" | "error"
  hideSubmit?: boolean
  contentSx?: SxProps<Theme>
  children: ReactNode
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      disableEnforceFocus
      disableRestoreFocus
      // 关闭瞬间移除、不播淡出 —— 父组件关闭时通常立刻清空数据源(editDraft=null),
      // 若留淡出动画,这 ~200ms 内弹窗内容已空 → 会闪一个空壳再消失。exit:0 根除,且对所有弹窗生效。
      transitionDuration={{ enter: 225, exit: 0 }}
    >
      <DialogTitle component="div" sx={{ pr: 6 }}>
        <Stack spacing={0.25}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{subtitle}</Typography>
          ) : null}
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="关闭"
          sx={{ position: "absolute", top: 12, right: 12, color: "text.secondary" }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={contentSx}>{children}</DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        {/* 校验集中区:opacity 切换、始终在 DOM 占位 → 出现/消失版面不跳 */}
        {footerLeft ?? <FormErrorChip errors={errors} />}
        {!hideSubmit ? (
          <Button
            variant="contained"
            color={submitColor}
            disableElevation
            disabled={submitDisabled}
            onClick={onSubmit}
            sx={{ flexShrink: 0 }}
          >
            {submitLabel}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}
